import type { NoteIdConfig } from '../exercises/note-identification/config.ts';
import type { RhythmConfig } from '../rhythm/config.ts';

/**
 * Built-in weekly plan templates offered to every teacher. Stable UUIDs so the
 * generated content migration is idempotent. Offsets are days from the plan's
 * Monday in the classroom timezone (release 07:00, due 23:59 local).
 */
export type TemplateActivity = {
  title: string;
  requiredSessions: number;
  masteryTarget: number | null;
  releaseDayOffset: number;
  dueDayOffset: number | null;
} & ({ exerciseType: 'note-identification'; config: NoteIdConfig } | { exerciseType: 'rhythm'; config: RhythmConfig });

export interface PlanTemplate {
  id: string;
  title: string;
  description: string;
  activities: TemplateActivity[];
}

function nid(partial: Partial<NoteIdConfig> & Pick<NoteIdConfig, 'clef' | 'range'>): NoteIdConfig {
  return {
    accidentals: ['natural'],
    answerMode: 'letter',
    questionCount: 10,
    staffFilter: 'all',
    accidentalRate: 0.5,
    includeRareSpellings: false,
    hintsAllowed: true,
    ...partial,
  };
}

function staffWeek(clef: NoteIdConfig['clef'], low: string, high: string): TemplateActivity[] {
  return [
    {
      title: 'Line notes',
      exerciseType: 'note-identification',
      config: nid({ clef, range: { low, high }, staffFilter: 'lines' }),
      requiredSessions: 1,
      masteryTarget: null,
      releaseDayOffset: 0,
      dueDayOffset: 2,
    },
    {
      title: 'Space notes',
      exerciseType: 'note-identification',
      config: nid({ clef, range: { low, high }, staffFilter: 'spaces' }),
      requiredSessions: 1,
      masteryTarget: null,
      releaseDayOffset: 1,
      dueDayOffset: 3,
    },
    {
      title: 'The whole staff',
      exerciseType: 'note-identification',
      config: nid({ clef, range: { low, high }, questionCount: 12 }),
      requiredSessions: 2,
      masteryTarget: 0.8,
      releaseDayOffset: 2,
      dueDayOffset: 4,
    },
  ];
}

function rhythm(partial: Partial<RhythmConfig>): RhythmConfig {
  return {
    timeSignature: '4/4',
    tempo: 80,
    measures: 2,
    noteValues: ['half', 'quarter', 'eighth'],
    dotted: false,
    rests: false,
    ties: false,
    triplets: false,
    questionCount: 6,
    ...partial,
  };
}

/** A three-step rhythm week: two focused drills, then a check that combines them. */
function rhythmWeek(steps: [string, Partial<RhythmConfig>][], check: Partial<RhythmConfig>): TemplateActivity[] {
  return [
    ...steps.map(([title, config], i): TemplateActivity => ({
      title,
      exerciseType: 'rhythm',
      config: rhythm(config),
      requiredSessions: 1,
      masteryTarget: null,
      releaseDayOffset: i,
      dueDayOffset: i + 2,
    })),
    {
      title: 'Rhythm check',
      exerciseType: 'rhythm',
      config: rhythm({ questionCount: 8, ...check }),
      requiredSessions: 2,
      masteryTarget: 0.8,
      releaseDayOffset: 2,
      dueDayOffset: 4,
    },
  ];
}

export const PLAN_TEMPLATES: readonly PlanTemplate[] = [
  {
    id: '6f1d1c1e-0000-4000-8000-000000000001',
    title: 'Treble clef: lines and spaces',
    description: 'Three short activities covering the five-line treble staff, due through Friday.',
    activities: staffWeek('treble', 'E4', 'F5'),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000002',
    title: 'Bass clef: lines and spaces',
    description: 'Three short activities covering the five-line bass staff, due through Friday.',
    activities: staffWeek('bass', 'G2', 'A3'),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000003',
    title: 'Alto clef: lines and spaces',
    description: 'Three short activities for violists and alto-clef readers.',
    activities: staffWeek('alto', 'F3', 'G4'),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000004',
    title: 'Tenor clef: lines and spaces',
    description: 'Three short activities for cello, bassoon and trombone tenor-clef reading.',
    activities: staffWeek('tenor', 'D3', 'E4'),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000005',
    title: 'Treble clef: ledger lines',
    description: 'Ledger lines below and above the treble staff, then a mixed check.',
    activities: [
      {
        title: 'Ledger lines below',
        exerciseType: 'note-identification',
        config: nid({ clef: 'treble', range: { low: 'F3', high: 'E4' } }),
        requiredSessions: 1,
        masteryTarget: null,
        releaseDayOffset: 0,
        dueDayOffset: 2,
      },
      {
        title: 'Ledger lines above',
        exerciseType: 'note-identification',
        config: nid({ clef: 'treble', range: { low: 'F5', high: 'E6' } }),
        requiredSessions: 1,
        masteryTarget: null,
        releaseDayOffset: 1,
        dueDayOffset: 3,
      },
      {
        title: 'Full range check',
        exerciseType: 'note-identification',
        config: nid({ clef: 'treble', range: { low: 'F3', high: 'E6' }, questionCount: 15 }),
        requiredSessions: 1,
        masteryTarget: 0.85,
        releaseDayOffset: 3,
        dueDayOffset: 4,
      },
    ],
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000006',
    title: 'Treble clef: sharps and flats',
    description: 'Spell sharps and flats on the treble staff.',
    activities: [
      {
        title: 'Sharps',
        exerciseType: 'note-identification',
        config: nid({ clef: 'treble', range: { low: 'E4', high: 'F#5' }, accidentals: ['natural', 'sharp'], answerMode: 'spelled', accidentalRate: 0.6 }),
        requiredSessions: 1,
        masteryTarget: null,
        releaseDayOffset: 0,
        dueDayOffset: 2,
      },
      {
        title: 'Flats',
        exerciseType: 'note-identification',
        config: nid({ clef: 'treble', range: { low: 'Eb4', high: 'F5' }, accidentals: ['natural', 'flat'], answerMode: 'spelled', accidentalRate: 0.6 }),
        requiredSessions: 1,
        masteryTarget: null,
        releaseDayOffset: 1,
        dueDayOffset: 3,
      },
      {
        title: 'Sharps and flats mixed',
        exerciseType: 'note-identification',
        config: nid({
          clef: 'treble',
          range: { low: 'Eb4', high: 'F#5' },
          accidentals: ['natural', 'sharp', 'flat'],
          answerMode: 'spelled',
          questionCount: 12,
        }),
        requiredSessions: 2,
        masteryTarget: 0.8,
        releaseDayOffset: 2,
        dueDayOffset: 4,
      },
    ],
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000007',
    title: 'Rhythm: steady beat and rests (4/4)',
    description: 'Tap quarters and eighths, then count through rests, then a check that mixes both.',
    activities: rhythmWeek(
      [
        ['Quarters and eighths', {}],
        ['Counting rests', { rests: true }],
      ],
      { rests: true },
    ),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000008',
    title: 'Rhythm: dotted notes and ties (4/4)',
    description: 'Long–short dotted figures, then ties across the beat, then a mixed check.',
    activities: rhythmWeek(
      [
        ['Dotted rhythms', { tempo: 72, rests: true, dotted: true }],
        ['Ties across the beat', { tempo: 72, rests: true, ties: true }],
      ],
      { tempo: 72, rests: true, dotted: true, ties: true },
    ),
  },
  {
    id: '6f1d1c1e-0000-4000-8000-000000000009',
    title: 'Rhythm: compound time (6/8)',
    description: 'Feel two dotted-quarter beats per bar, add rests, then a check.',
    activities: rhythmWeek(
      [
        ['Two beats in 6/8', { timeSignature: '6/8', tempo: 60 }],
        ['Rests in 6/8', { timeSignature: '6/8', tempo: 60, rests: true }],
      ],
      { timeSignature: '6/8', tempo: 60, rests: true },
    ),
  },
];
